/**
 * Repository-scan linkage helpers (issue #2000, ADR-2000).
 *
 * The backend owns the manifest of scan-managed fields
 * (`Service.scanManagedFields`); this module only interprets it for display.
 */

import type { ScanManagedField, Service } from '../graphql/types';

/** Scanner onboarding guide the call to action links to (ADR-2000 decision 7). */
export const SCANNER_ONBOARDING_URL =
  'https://github.com/tyler-technologies-dsd/ea-repository-scanning/blob/main/repo-kit/ONBOARDING.md';

/** File in the source repo where scan-managed values are fixed permanently. */
export const APP_METADATA_PATH = '.tyler/app-metadata.yml';

/** Linked means `scannerAppId` is populated (ADR-2000). */
export function isScanLinked(service: Pick<Service, 'scannerAppId'>): boolean {
  return Boolean(service.scannerAppId);
}

/**
 * Link to the source repo's `.tyler/app-metadata.yml`, or null when the
 * Service has no usable https repository URL.
 */
export function appMetadataUrl(repo: string | null | undefined): string | null {
  if (!repo) {
    return null;
  }
  try {
    const parsed = new URL(repo);
    if (parsed.protocol !== 'https:') {
      return null;
    }
    const base = `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '').replace(/\.git$/, '');
    return `${base}/blob/HEAD/${APP_METADATA_PATH}`;
  } catch {
    return null;
  }
}

/** The manifest entry for a form field, if the scan manages it. */
export function findManagedField(
  fields: readonly ScanManagedField[] | null | undefined,
  field: string
): ScanManagedField | undefined {
  return fields?.find((entry) => entry.field === field);
}

/**
 * Per-field indicator text. One tier for ALWAYS and CONDITIONAL fields
 * (ADR-2000 decision 3); ADDITIVE fields get their own wording.
 */
export function managedFieldNote(entry: ScanManagedField): string {
  if (entry.mode === 'ADDITIVE') {
    return 'Types declared by the repository scan will be re-added if removed.';
  }
  return `Managed by repository scan. To change it permanently, update ${entry.sourcePath} in ${APP_METADATA_PATH}.`;
}

/** Stronger warning shown when a user changes a linked Service's team (ADR-2000 decision 4). */
export function teamChangeWarning(entry: ScanManagedField): string {
  return `Changing the team here will be undone by the next repository scan unless ${entry.sourcePath} in ${APP_METADATA_PATH} is changed too.`;
}

/** Form-level banner text. */
export function managedBannerText(repo: string | null | undefined): string {
  const target = repo ? `the repository scan of ${repo}` : 'a repository scan';
  return `This Service is linked to ${target}. Fields marked below are kept in sync with the repo, and changes here will be replaced on the next scan.`;
}

/** Dismissed-call-to-action persistence key, per Service. */
export function ctaDismissKey(serviceId: string): string {
  return `scan-link-cta-dismissed:${serviceId}`;
}
